import { gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { ScoutSettingsFragment } from '../fragments/ScoutSettings';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import {
  Header,
  Footer,
  Main,
  Container,
  ContentWrapper,
  EntryHeader,
  NavigationMenu,
  FeaturedImage,
  SEO,
  useSharedLayout,
  } from '../components';
import { generateOfficeUrl } from '../utils/officeUrlUtils';
import { getImageSizes, getImageDimensions, getImageQuality, getImagePriority, getBlurDataURL, getOptimizedImageUrl, orderImagesByType, processOfficeImages } from '../utils/imageUtils';
import OptimizedImage from '../components/OptimizedImage';
import { PageSEOFragment } from '../fragments/SEO';
import { NavigationItemFragment, HeaderMenuFragment, FooterMenuFragment } from '../fragments/NavigationFragment';
import { PageContentFragment, PageTranslationContentFragment } from '../fragments/PageContentFragment';
import { PageBaseFragment } from '../fragments/PageBaseFragment';
import { useQuery } from '@apollo/client';
import useArchiveStore from '../store/archiveStore';

// Lazy load heavy components to reduce initial bundle size
const ArchiveMap = dynamic(() => import('../components/Map/ArchiveMap'), {
  loading: () => <div className="h-96 animate-pulse bg-gray-200 rounded-lg flex items-center justify-center"><span className="text-gray-500">Loading map...</span></div>,
  ssr: false // Map isn't needed for SSR
});

const Banner = dynamic(() => import('../components/Banner/Banner'), {
  loading: () => <div className="h-32 animate-pulse bg-gray-200 rounded-lg"></div>
});

const ServiceSection = dynamic(() => import('../components/ServiceSection/ServiceSection'), {
  loading: () => <div className="h-64 animate-pulse bg-gray-200 rounded-lg"></div>
});

const ContactFormSection = dynamic(() => import('../components/ContactFormSection/ContactFormSection'), {
  loading: () => <div className="h-96 animate-pulse bg-gray-200 rounded-lg"></div>
});



// Mobile Collapsible Section Text Component (for section.subtext)
function MobileCollapsibleSectionText({ subtext, title }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!subtext) return null;
  
  return (
    <div className="block md:hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-4 text-left bg-transparent border-none"
      >
        <h3 className="text-lg font-semibold text-[var(--text-primary)] m-0">
         Les mer om  {title}
        </h3>
        <svg 
          className={`w-5 h-5 text-[var(--text-primary)] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isExpanded && (
        <div className="pb-4">
          <p className="text-lg md:text-xl box-texts">
            {subtext}
          </p>
        </div>
      )}
    </div>
  );
}

// Add ContactPopup component
function ContactPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const router = useRouter();
  const isEnglish = router.locale === 'en';
  
  const popupText = isEnglish 
    ? "Can't find what you're looking for? Contact us for a non-binding conversation."
    : "Finner du ikke det du leter etter? Kontakt oss for en uforpliktende prat.";
  
  const contactButtonText = isEnglish ? "Contact us" : "Kontakt oss";
  const closeButtonText = isEnglish ? "Close" : "Lukk";

  // Check if popup has been shown this session on component mount
  useEffect(() => {
    const sessionKey = 'contactPopupShown';
    const hasShownThisSession = sessionStorage.getItem(sessionKey) === 'true';
    if (hasShownThisSession) {
      setHasShown(true);
    }
  }, []);

  useEffect(() => {
    if (hasShown) return; // Don't show again if already shown

    let timeoutId;
    let scrollThreshold = false;

    // Show after 10 seconds OR after scrolling 50% of page
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50) {
        scrollThreshold = true;
        checkShowConditions();
      }
    };

    const showPopup = () => {
      if (!hasShown) {
        setIsVisible(true);
        setHasShown(true);
        // Mark as shown in session storage
        sessionStorage.setItem('contactPopupShown', 'true');
      }
    };

    const checkShowConditions = () => {
      if (scrollThreshold && !hasShown) {
        showPopup();
      }
    };

    // Timer for 10 seconds
    timeoutId = setTimeout(() => {
      if (!hasShown) {
        showPopup();
      }
    }, 10000);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasShown]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleContact = () => {
    const contactUrl = isEnglish ? '/en/contact-us' : '/kontakt-oss';
    router.push(contactUrl);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-6 right-6 max-w-sm bg-white border border-gray-200 rounded-lg shadow-2xl p-6 z-50 transition-all duration-300 ${
        isClosing ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      }`}
      style={{
        transform: isClosing ? 'translateX(100%)' : 'translateX(0)',
      }}
    >
      <button
        onClick={handleClose}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label={closeButtonText}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div className="pr-6">
        <p className="text-gray-700 mb-4 text-sm leading-relaxed">
          {popupText}
        </p>
        
        <button
          onClick={handleContact}
          className="button-base button-primary text-sm px-4 py-2 w-full"
        >
          {contactButtonText}
        </button>
      </div>
    </div>
  );
}

// GraphQL query for fetching offices by area
const GET_OFFICES_BY_AREA = gql`
  query GetOfficesByArea($areaSlug: String!, $language: LanguageCodeFilterEnum!, $first: Int = 12, $after: String) {
    kontorer(
      first: $first,
      after: $after,
      where: {
        language: $language,
        taxQuery: {
          taxArray: [{
            taxonomy: OMRADE,
            terms: [$areaSlug],
            field: SLUG,
            operator: IN
          }]
        }
      }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        databaseId
        title
        uri
        slug
        terms {
          nodes {
            ... on Omrade {
              id
              name
              slug
            }
          }
        }
        kontorer {
          kontornavn
          bilde {
            bilde {
              node {
                mediaItemUrl
              }
            }
            type
          }
          antallAnsatte {
            fra
            til
          }
          informasjon {
            areal
            kontorplasser
            kortBeskrivelse
            type
            leie
          }
          fasiliteter {
            leggTilFasiliteter {
              fasilitetsnavn
            }
          }
          lokasjon {
            kart {
              streetAddress
              streetName
              streetNumber
              city
              latitude
              longitude
            }
            omrade {
              nodes {
                name
                slug
              }
            }
          }
        }
      }
    }
  }
`;

// GraphQL query for getting all areas to detect if current page is an area page
const GET_ALL_AREAS = gql`
  query GetAllAreas($language: LanguageCodeFilterEnum!) {
    omrader(first: 100, where: { language: $language }) {
      nodes {
        id
        slug
        name
        count
      }
    }
  }
`;

// Helper function to check if current page is FAQ
const isFaqPage = (router, activeContent) => {
  return router.asPath === '/faq' || router.asPath === '/faq/' || 
         router.asPath === '/en/spacefinder-faq' || router.asPath === '/en/spacefinder-faq/' ||
         activeContent?.slug === 'faq' || activeContent?.slug === 'spacefinder-faq' ||
         activeContent?.uri?.includes('faq');
};

// Helper function to check if current page is Partners (samarbeidspartnere)
const isPartnersPage = (router, activeContent) => {
  const normalizedPath = (router.asPath || '').replace(/\/+$/, '');
  if (normalizedPath === '/samarbeidspartnere' || normalizedPath === '/en/partners') {
    return true;
  }
  if (activeContent?.slug === 'samarbeidspartnere' || activeContent?.slug === 'partners') {
    return true;
  }
  const uri = activeContent?.uri || '';
  return uri.includes('samarbeidspartnere') || uri.includes('/en/partners');
};

// Pages that should use the FAQ-like custom container
const isFaqOrPartnersPage = (router, activeContent) => {
  return isFaqPage(router, activeContent) || isPartnersPage(router, activeContent);
};

export default function Component(props) {
  const router = useRouter();
  const [isAreaPage, setIsAreaPage] = useState(false);
  const [areaSlug, setAreaSlug] = useState('');
  const [areaTotalCount, setAreaTotalCount] = useState(null);

  const [connInfo, setConnInfo] = useState({ hasNextPage: false, endCursor: null });

  // **FILTERING STATE - Same as archive page**
  const {
    viewType,
    setViewType,
    showMap,
    setShowMap,
    selectedImageType,
    setSelectedImageType,
    setEmployeeCount,
    employeeCount,
    appliedEmployeeCount,
    setAppliedEmployeeCount,
    lastPageType,
    setLastPageType
  } = useArchiveStore();
  
  // State for offices data and pagination
  const [allOffices, setAllOffices] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingAllOffices, setIsLoadingAllOffices] = useState(false);
  
  // NEW: State for load more functionality
  const [displayedOffices, setDisplayedOffices] = useState([]);
  const [currentDisplayCount, setCurrentDisplayCount] = useState(6); // Start with 6 offices
  const [hasMoreToShow, setHasMoreToShow] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0); // Track filtered count for display
  const INITIAL_LOAD_COUNT = 6;
  const LOAD_MORE_COUNT = 6;
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [isImageTypeDropdownOpen, setIsImageTypeDropdownOpen] = useState(false);
  const [hoveredImages, setHoveredImages] = useState({});
  const filterBarRef = useRef(null);
  const [showStickyFilterBar, setShowStickyFilterBar] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const employeeDropdownRef = useRef(null);
  const imageTypeDropdownRef = useRef(null);
  const countSectionRef = useRef(null);
  const [showMobileFilterBar, setShowMobileFilterBar] = useState(false);
  const initialFilterStateRef = useRef(null);
  const [isViewTypeChanging, setIsViewTypeChanging] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle compact sticky filter bar once original filter row scrolls past header
  useEffect(() => {
    const headerHeight = 80; // match header height
    const onScroll = () => {
      if (!filterBarRef.current) return;
      const rect = filterBarRef.current.getBoundingClientRect();
      const shouldShowSticky = rect.bottom <= headerHeight;
      
      // Close any open dropdowns when sticky filter becomes active (desktop only)
      if (shouldShowSticky && window.innerWidth >= 768) {
        setIsEmployeeDropdownOpen(false);
        setIsImageTypeDropdownOpen(false);
      }
      
      setShowStickyFilterBar(shouldShowSticky);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Show mobile filter only after scrolling below the count header on area pages
  useEffect(() => {
    const headerHeight = 80; // align with site header height
    const onScroll = () => {
      if (!countSectionRef.current) return;
      const rect = countSectionRef.current.getBoundingClientRect();
      setShowMobileFilterBar(rect.bottom <= headerHeight);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Prevent body scroll when mobile filter is open
  useEffect(() => {
    if (isMobileFilterOpen && isMobile) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      
      // Store initial filter state when opening (only if not already set)
      if (!initialFilterStateRef.current) {
        initialFilterStateRef.current = {
          appliedEmployeeCount,
          selectedImageType
        };
      }
      
      // Disable smooth scrolling temporarily
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      
      // Prevent scrolling by setting body to fixed position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';
      
      return () => {
        // Get the stored scroll position from the body top value
        const bodyTop = parseInt(document.body.style.top || '0', 10);
        const scrollPosition = -bodyTop;
        
        // Remove the fixed positioning styles
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.left = '';
        document.body.style.right = '';
        
        // Check if filter values changed
        const hasFilterChanges = initialFilterStateRef.current && (
          appliedEmployeeCount !== initialFilterStateRef.current.appliedEmployeeCount ||
          selectedImageType !== initialFilterStateRef.current.selectedImageType
        );
        
        if (hasFilterChanges) {
          // Scroll to top when filter values changed
          window.scrollTo(0, 0);
        } else {
          // Restore original position when no changes
          window.scrollTo(0, scrollPosition);
        }
        
        // Restore original scroll behavior
        document.documentElement.style.scrollBehavior = originalScrollBehavior;
        
        // Clear initial filter state
        initialFilterStateRef.current = null;
      };
    }
  }, [isMobileFilterOpen, isMobile, appliedEmployeeCount, selectedImageType]);

  // Handle image hover
  const handleImageHover = (officeId, image) => {
    setHoveredImages(prev => ({
      ...prev,
      [officeId]: image
    }));
  };

  // Handle image leave
  const handleImageLeave = (officeId) => {
    setHoveredImages(prev => ({
      ...prev,
      [officeId]: null
    }));
  };

  // Handle view type changes to prevent scroll jump on mobile
  useEffect(() => {
    if (isMobile && isViewTypeChanging) {
      // Add a small delay to let the layout settle, then scroll to results
      const timer = setTimeout(() => {
        if (countSectionRef.current) {
          const rect = countSectionRef.current.getBoundingClientRect();
          const headerHeight = 80; // Site header height
          const targetPosition = window.scrollY + rect.top - headerHeight - 20; // 20px buffer
          
          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth'
          });
        }
        setIsViewTypeChanging(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [viewType, isMobile, isViewTypeChanging]);
  
  // Custom setViewType wrapper to handle mobile scroll
  const handleViewTypeChange = (newViewType) => {
    if (isMobile && newViewType !== viewType) {
      setIsViewTypeChanging(true);
    }
    setViewType(newViewType);
  };

  // **RESET FILTERS when switching between archive and area pages**
  useEffect(() => {
    // Only set page type if this is actually an area page
    if (isAreaPage) {
      const currentPageType = 'area';
      
      // If we're switching from archive page to area page, reset filters
      if (lastPageType === 'archive' && currentPageType === 'area') {
        setAppliedEmployeeCount('');
        setEmployeeCount('');
        setSelectedImageType('alle');
        // Keep view preferences (viewType, showMap) as they can carry over
      }
      
      // Update the last page type
      setLastPageType(currentPageType);
    }
  }, [isAreaPage, lastPageType, setLastPageType, setAppliedEmployeeCount, setEmployeeCount, setSelectedImageType]);

  // Define language before using it in queries
  const language = router.locale?.toUpperCase() === 'NB' ? 'NB' : 'EN';

  // Query for all areas to detect if current page is an area page
  const { data: areasData, error: areasError, refetch: refetchAreas } = useQuery(GET_ALL_AREAS, {
    variables: { language },
    onCompleted: (data) => {
      const areas = data?.omrader?.nodes || [];
      
      // Extract page slug from URI, handling multilingual paths
      let pageSlug = slug;
      if (!pageSlug && uri) {
        // Remove leading/trailing slashes and language prefixes
        pageSlug = uri
          .replace(/^\/+|\/+$/g, '')  // Remove leading/trailing slashes
          .replace(/^(en|nb)\//, '')   // Remove language prefix
          .replace(/-en$/, '')         // Remove -en suffix
          .replace(/-nb$/, '');        // Remove -nb suffix
      }
      
      const currentArea = areas.find(area => {
        // First try exact slug match
        if (area.slug === pageSlug) {
          return true;
        }
        // Then try with language suffix
        if (area.slug === `${pageSlug}-${language.toLowerCase()}`) {
          return true;
        }
        // Finally try name match
        if (area.name && area.name.toLowerCase() === pageSlug) {
          return true;
        }
        return false;
      });
      
      if (currentArea) {
        setIsAreaPage(true);
        setAreaSlug(currentArea.slug); // Keep original slug for GraphQL query
        setAreaTotalCount(currentArea.count ?? null);
      }
    },
    onError: (error) => {
      console.error('Areas Query Error:', error);
    }
  });

  // Query for offices in this area
  const { data: officesData, loading: officesLoading, fetchMore, refetch: refetchOffices } = useQuery(GET_OFFICES_BY_AREA, {
    variables: { 
      areaSlug: areaSlug || '', 
      language,
      first: INITIAL_LOAD_COUNT
    },
    skip: !isAreaPage || !areaSlug,
    onCompleted: (data) => {
      if (!isAreaPage || !areaSlug) return;
    
      const firstNodes = data?.kontorer?.nodes ?? [];
      const pageInfo   = data?.kontorer?.pageInfo ?? {};
    
      setAllOffices(firstNodes);
      setDisplayedOffices(firstNodes);
      setHasMoreToShow(!!pageInfo.hasNextPage);
    
      setConnInfo({
        hasNextPage: !!pageInfo.hasNextPage,
        endCursor: pageInfo.endCursor ?? null,
      });
    },
    onError: (error) => {
      console.error('Offices Query Error:', error);
      setError(error);
    }
  });

  // Listen for language changes and refetch both areas and office data
  useEffect(() => {
    const handleLanguageChange = async (event) => {
      const { newLocale } = event.detail;
      const newLanguage = newLocale?.toUpperCase() === 'NB' ? 'NB' : 'EN';
      
      try {
        console.log('[Area Page] Refetching areas for new language:', newLanguage);
        await refetchAreas({
          language: newLanguage
        });
        
        if (isAreaPage && areaSlug) {
          console.log('[Area Page] Refetching offices for new language:', newLanguage);
          await refetchOffices({
            areaSlug,
            language: newLanguage,
            first: INITIAL_LOAD_COUNT
          });
        }
      } catch (error) {
        console.error('Error refetching data after language change:', error);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, [isAreaPage, areaSlug, refetchAreas, refetchOffices]);

  // **AUTO-LOAD ALL OFFICES on page load for instant filtering**
  useEffect(() => {
    // Load ALL offices when area page is detected (not waiting for filter)
    // This ensures filtering is instant without the delay
    if (isAreaPage && areaSlug && connInfo.hasNextPage && !officesLoading && !isLoadingAllOffices) {
      const loadAllOffices = async () => {
        setIsLoadingAllOffices(true);
        try {
          let cursor = connInfo.endCursor;
          let hasMore = connInfo.hasNextPage;
          let allLoadedOffices = [...allOffices];
          
          console.log(`[Area Page] Loading all offices for ${areaSlug}...`);
          
          while (hasMore && cursor) {
            const res = await fetchMore({
              variables: {
                areaSlug,
                language,
                first: 100, // Load in bigger batches for faster loading
                after: cursor,
              },
            });
            
            const newNodes = res?.data?.kontorer?.nodes || [];
            const newPageInfo = res?.data?.kontorer?.pageInfo || {};
            
            allLoadedOffices = [...allLoadedOffices, ...newNodes];
            hasMore = !!newPageInfo.hasNextPage;
            cursor = newPageInfo.endCursor;
          }
          
          console.log(`[Area Page] Loaded ${allLoadedOffices.length} total offices`);
          
          // Update state with all offices
          setAllOffices(allLoadedOffices);
          setConnInfo({ hasNextPage: false, endCursor: null });
        } catch (e) {
          console.error('Failed to load all offices:', e);
        } finally {
          setIsLoadingAllOffices(false);
        }
      };
      
      loadAllOffices();
    }
  }, [isAreaPage, areaSlug, connInfo.hasNextPage, connInfo.endCursor, officesLoading, isLoadingAllOffices, allOffices, fetchMore, language]);

  // **CLIENT-SIDE FILTERING: Apply filters instantly when selection changes**
  useEffect(() => {
      if (allOffices.length >= 0) {
        const filtered = filterOfficesClientSide(allOffices, appliedEmployeeCount, selectedImageType);
        
        // Always use pagination - show currentDisplayCount items at a time
        const officesToDisplay = filtered.slice(0, currentDisplayCount);
        
        setDisplayedOffices(officesToDisplay);
        // Show "Load More" if there are more filtered results OR more on server
        setHasMoreToShow((connInfo.hasNextPage) || (filtered.length > officesToDisplay.length));
        setFilteredCount(filtered.length);
      }
    }, [allOffices, appliedEmployeeCount, selectedImageType, currentDisplayCount, connInfo]);
  
  // Lightbox state - same as archive page
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  
  // For scrolling active modal thumbnail into view - same as archive page
  const modalThumbRefs = useRef([]);

  useEffect(() => {
    if (lightboxOpen && modalThumbRefs.current[lightboxImageIndex]) {
      modalThumbRefs.current[lightboxImageIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [lightboxImageIndex, lightboxOpen]);

  // Calculate windowed thumbnails for modal (show 5, centered on active) - same as archive page
  const getModalThumbWindow = () => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, lightboxImageIndex - half);
    let end = start + windowSize;
    if (end > lightboxImages.length) {
      end = lightboxImages.length;
      start = Math.max(0, end - windowSize);
    }
    return [start, end];
  };

  // Get full-size image URL for lightbox - force highest quality
  const getLightboxImageUrl = (image) => {
    if (!image?.bilde?.node) return '';
    
    const node = image.bilde.node;
    
    // Use mediaItemUrl as primary source - this should be the original file
    let url = node.mediaItemUrl;
    
    if (!url) return '';
    
    // Clean URL from WordPress compression and sizing parameters
    url = url.replace(/(\?|&)quality=\d+/g, '');
    url = url.replace(/(\?|&)w=\d+/g, '');
    url = url.replace(/(\?|&)h=\d+/g, '');
    url = url.replace(/(\?|&)resize=[\d,]+/g, '');
    
    // Remove WordPress automatic sizing from filename if present
    // e.g., "image-683x1024.jpg" -> "image.jpg"
    url = url.replace(/-\d+x\d+(\.[\w]+)$/g, '$1');
    
    // Force maximum quality and disable compression
    return url.includes('?') ? `${url}&quality=100&strip=false` : `${url}?quality=100&strip=false`;
  };

  // Get thumbnail URL for small images
  const getThumbnailUrl = (image) => {
    if (!image?.bilde?.node) return '';
    const node = image.bilde.node;
    
    // For thumbnails, use smaller sizes if available
    if (node.sizes && node.sizes.length > 0) {
      const mediumSize = node.sizes.find(size => size.name === 'medium' || size.name === 'thumbnail');
      if (mediumSize && mediumSize.sourceUrl) {
        return mediumSize.sourceUrl;
      }
    }
    
    // Fallback to main URL with size restriction for thumbnails
    let url = node.mediaItemUrl || node.sourceUrl;
    return url ? `${url}?w=300&h=200&quality=85` : '';
  };

  // Toggle lightbox overlay
  const toggleLightbox = (isOpen, images = [], startIndex = 0) => {
    setLightboxOpen(isOpen);
    if (isOpen && images.length > 0) {
      // Pre-process URLs once to ensure consistent caching
      const processedImages = images.map(img => ({
        ...img,
        processedLightboxUrl: getLightboxImageUrl(img),
        processedThumbnailUrl: getThumbnailUrl(img)
      }));
      
      // Preload all images for instant navigation
      processedImages.forEach(img => {
        if (img.processedLightboxUrl) {
          const preloadImg = new window.Image();
          preloadImg.src = img.processedLightboxUrl;
        }
      });
      
      setLightboxImages(processedImages);
      setLightboxImageIndex(startIndex);
    } else {
      setLightboxImages([]);
      setLightboxImageIndex(0);
    }
  };

  // Navigate lightbox images
  const navigateLightbox = (direction) => {
    if (lightboxImages.length === 0) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = lightboxImageIndex === 0 ? lightboxImages.length - 1 : lightboxImageIndex - 1;
    } else {
      newIndex = lightboxImageIndex === lightboxImages.length - 1 ? 0 : lightboxImageIndex + 1;
    }
    setLightboxImageIndex(newIndex);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        toggleLightbox(false);
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox('prev');
      } else if (e.key === 'ArrowRight') {
        navigateLightbox('next');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImageIndex, lightboxImages.length]);

  // Loading state for previews
  if (props.loading) {
    return <>Loading...</>;
  }

  const { title: siteTitle, description: siteDescription } = props?.data?.generalSettings;
  
  // Get shared layout data from context (cached across pages)
  const { headerMenuItems, footerMenuItems, scoutSettings } = useSharedLayout();
  const primaryMenu = headerMenuItems ?? [];
  const footerMenu = footerMenuItems ?? [];
  
  // Get the page data and its translations
  const pageData = props?.data?.page;
  const translations = pageData?.translations || [];
  
  // Find translation for current language
  const currentTranslation = translations.find(t => t.language?.code?.toLowerCase() === router.locale);
  
  // Use translated content if available, otherwise use original
  const activeContent = currentTranslation || pageData;

  const { 
    title, 
    content,
    featuredImage,
    pages,
    uri,
    slug,
    databaseId
  } = activeContent ?? {};

  const seoData = activeContent?.seo;

  // **CLIENT-SIDE FILTERING: Apply filters instantly when selection changes**
  const filterOfficesClientSide = (offices, employeeCount, imageType) => {
    let filtered = [...offices];
    
    // Filter by employee count
    if (employeeCount) {
      const targetCount = parseInt(employeeCount);
      filtered = filtered.filter(office => {
        const minEmployees = office.kontorer?.antallAnsatte?.fra || 0;
        const maxEmployees = office.kontorer?.antallAnsatte?.til || 999;
        return targetCount >= minEmployees && targetCount <= maxEmployees;
      });
    }
    
    // Do NOT filter by image type; we only prioritize ordering of images per office
    
    return filtered;
  };

  // **FILTER HANDLERS - Same as archive page**
  const handleEmployeeFilter = () => {
    if (employeeCount) {
      const count = parseFloat(employeeCount);
      if (!isNaN(count) && count > 0) {
        setAppliedEmployeeCount(count.toString());
        setIsEmployeeDropdownOpen(false);
        // Reset display count when applying filter
        setCurrentDisplayCount(INITIAL_LOAD_COUNT);
        
        // Scroll to results section
        setTimeout(() => {
          const resultsSection = countSectionRef.current;
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  };

  // **Reset employee filter**
  const handleEmployeeFilterReset = () => {
    setEmployeeCount('');
    setAppliedEmployeeCount('');
    setIsEmployeeDropdownOpen(false);
    // Reset display count when resetting filter
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    
    // Scroll to results section
    setTimeout(() => {
      const resultsSection = countSectionRef.current;
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Auto-apply employee filter with debounce
  useEffect(() => {
    if (!employeeCount) {
      // Clear filter if input is empty
      setAppliedEmployeeCount('');
      setCurrentDisplayCount(INITIAL_LOAD_COUNT); // Reset to show first page when clearing
      return;
    }

    const count = parseFloat(employeeCount);
    if (isNaN(count) || count <= 0) {
      return;
    }

    // Debounce: wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      setAppliedEmployeeCount(count.toString());
      setCurrentDisplayCount(INITIAL_LOAD_COUNT); // Reset to show first page of filtered results
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [employeeCount]);

  // Scroll to top when employee filter is applied
  useEffect(() => {
    if (appliedEmployeeCount && countSectionRef.current) {
      setTimeout(() => {
        const resultsSection = countSectionRef.current;
        if (resultsSection) {
          const rect = resultsSection.getBoundingClientRect();
          const headerHeight = 80; // Site header height
          const targetPosition = window.scrollY + rect.top - headerHeight - 20; // 20px buffer
          
          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [appliedEmployeeCount]);

  // NEW: Load more function
  const handleLoadMore = async () => {
    // If filter is active, we've already loaded all offices, so no server fetch needed
    if (appliedEmployeeCount) {
      // Just reveal more locally filtered items
      const filtered = filterOfficesClientSide(allOffices, appliedEmployeeCount, selectedImageType);
      if (filtered.length > currentDisplayCount) {
        setCurrentDisplayCount(prev => prev + LOAD_MORE_COUNT);
      }
      return;
    }
    
    // If local slice still hides some already-loaded items, just reveal them
    const filtered = filterOfficesClientSide(allOffices, appliedEmployeeCount, selectedImageType);
    if (filtered.length > currentDisplayCount) {
      setCurrentDisplayCount(prev => prev + LOAD_MORE_COUNT);
      return;
    }

    // Otherwise, pull next page from the server
    if (!connInfo.hasNextPage || !connInfo.endCursor) return;
    try {
      const res = await fetchMore({
        variables: {
          areaSlug,
          language,
          first: LOAD_MORE_COUNT,
          after: connInfo.endCursor,
        },
      });
      const newNodes = res?.data?.kontorer?.nodes || [];
      const newPageInfo = res?.data?.kontorer?.pageInfo || {};

      const nextAll = [...allOffices, ...newNodes];
      setAllOffices(nextAll);
      setConnInfo({ hasNextPage: !!newPageInfo.hasNextPage, endCursor: newPageInfo.endCursor ?? null });

      // Bump how many we show
      setCurrentDisplayCount(prev => prev + LOAD_MORE_COUNT);
    } catch (e) {
      console.error('Load more failed:', e);
    }
  };

  // Handle image type change
  const handleImageTypeChange = (imageType) => {
    setSelectedImageType(imageType);
    setHoveredImages({}); // Clear hover states when changing image type
    setIsImageTypeDropdownOpen(false); // Close dropdown after selection
    // Reset display count when changing image type
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    
    // Scroll to results section
    setTimeout(() => {
      const resultsSection = countSectionRef.current;
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const getImageTypeLabel = () => {
    const selectedType = imageTypes.find(type => type.value === selectedImageType);
    return selectedType ? selectedType.label : t.imageTypes.allImages;
  };

  // Handle view type toggle
  const handleViewTypeToggle = () => {
    handleViewTypeChange(viewType === 'grid' ? 'list' : 'grid');
  };

  // Handle map toggle
  const handleMapToggle = () => {
    setShowMap(!showMap);
  };

  // Handle reset all filters
  const handleResetAllFilters = () => {
    setEmployeeCount('');
    setAppliedEmployeeCount('');
    setSelectedImageType('alle');
    setIsEmployeeDropdownOpen(false);
    // Reset display count when resetting filters
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    
    // Scroll to results section
    setTimeout(() => {
      const resultsSection = countSectionRef.current;
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // **TRANSLATIONS for filters**
  const t = {
    filters: {
      employees: {
        select: language === 'NB' ? 'Velg antall ansatte' : 'Select number of employees',
        employeeCount: language === 'NB' ? 'Antall ansatte' : 'Number of employees',
        count: language === 'NB' ? 'ansatte' : 'employees',
        filter: language === 'NB' ? 'Filtrer' : 'Filter'
      },
      reset: language === 'NB' ? 'Nullstill' : 'Reset'
    },
    imageTypes: {
      allImages: language === 'NB' ? 'Bildevisning' : 'Image view',
      floorplan: language === 'NB' ? 'Plantegning' : 'Floorplan',
      facade: language === 'NB' ? 'Fasade' : 'Facade',
      office: language === 'NB' ? 'Kontor' : 'Office',
      meetingRoom: language === 'NB' ? 'Møterom' : 'Meeting Room',
      facilities: language === 'NB' ? 'Fasiliteter' : 'Facilities'
    },
    viewTypes: {
      grid: language === 'NB' ? 'Rutenett' : 'Grid',
      list: language === 'NB' ? 'Liste' : 'List',
      showMap: language === 'NB' ? 'Kart' : 'Map',
      hideMap: language === 'NB' ? 'Skjul kart' : 'Hide Map'
    }
  };

  const imageTypes = [
    { value: 'alle', label: t.imageTypes.allImages },
    { value: 'fasade', label: t.imageTypes.facade },
    { value: 'kontor', label: t.imageTypes.office },
    { value: 'moterom', label: t.imageTypes.meetingRoom },
  ];

  const getEmployeeFilterLabel = () => {
    if (appliedEmployeeCount) {
      return `${appliedEmployeeCount} ${t.filters.employees.count}`;
    }
    return t.filters.employees.select;
  };

  // **Dropdown close logic**
  useEffect(() => {
    function handleClickOutside(event) {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setIsEmployeeDropdownOpen(false);
      }
      if (imageTypeDropdownRef.current && !imageTypeDropdownRef.current.contains(event.target)) {
        setIsImageTypeDropdownOpen(false);
      }
    }

    const events = ['pointerdown', 'mousedown', 'touchstart'];
    events.forEach((evt) => document.addEventListener(evt, handleClickOutside));
    return () => {
      events.forEach((evt) => document.removeEventListener(evt, handleClickOutside));
    };
  }, []);

  // Effect to update URL when language changes
  useEffect(() => {
    if (router.locale && translations.length > 0) {
      const translation = translations.find(t => t.language?.code?.toLowerCase() === router.locale);
      if (translation?.uri && translation.uri !== router.asPath) {
        // Skip redirect for new SEO-optimized area page URLs to prevent infinite loops
        const isNewAreaPageStructure = router.asPath.includes('/offices/for-rent/') || router.asPath.includes('/kontorer/til-leie/');
        
        if (!isNewAreaPageStructure) {
          let targetUri = translation.uri;
          
          // For Norwegian, remove the -nb suffix from area page URIs
          if (router.locale === 'nb' && targetUri.includes('-nb/')) {
            targetUri = targetUri.replace('-nb/', '/');
          }
          
          router.push(targetUri, targetUri, { locale: router.locale });
        }
      }
    }
  }, [router.locale, translations]);

  const pageContent = pages?.content || [];
  const heroContent = pageContent?.find(section => section.fieldGroupName === 'PagesContentHeroLayout');
  const sections = pageContent?.filter(section => 
    section.fieldGroupName === 'PagesContentSectionImageLeftTextRightLayout' || 
    section.fieldGroupName === 'PagesContentSectionImageRightTextLeftLayout'
  ) || [];

  // Check if this is one of the special pages
  const isSpecialPage = databaseId === 19778 || databaseId === 14;

  const frontendDomain = process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://spacefinder.no';
  
  // Fix canonical URL for English area pages to include /en prefix
  let canonicalUrl;
  if (router.asPath === '/') {
    canonicalUrl = frontendDomain;
  } else {
    // Check if this is an English area page that needs /en prefix
    const isEnglishAreaPage = router.locale === 'en' && router.asPath.match(/^\/offices\/for-rent\/[^\/]+\/?$/);
    
    if (isEnglishAreaPage) {
      // Add /en prefix and ensure trailing slash
      const cleanPath = router.asPath.endsWith('/') ? router.asPath : `${router.asPath}/`;
      canonicalUrl = `${frontendDomain}/en${cleanPath}`;
    } else {
      // Use router.asPath as-is, but add trailing slash if missing
      const cleanPath = router.asPath.endsWith('/') ? router.asPath : `${router.asPath}/`;
      canonicalUrl = `${frontendDomain}${cleanPath}`;
    }
  }

  // Get offices data
  const offices = allOffices || [];
  const hasOffices = displayedOffices.length > 0;
  
  // Get area name for display
  const areaName = isAreaPage && areasData?.omrader?.nodes?.find(area => area.slug === areaSlug)?.name || areaSlug;

  // For English area pages, completely override WordPress SEO to prevent duplicates
  const isEnglishAreaPage = router.locale === 'en' && router.asPath.match(/^\/offices\/for-rent\/[^\/]+\/?$/);
  
  return (
    <>
      {isEnglishAreaPage && (
        <Head>
          {/* Completely override WordPress SEO for English area pages */}
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:url" content={canonicalUrl} />
          <title>{seoData?.title || `Offices for rent in ${router.query.område || 'area'} | Spacefinder`}</title>
          <meta name="description" content={seoData?.description || `Find office spaces for rent in ${router.query.område || 'this area'}. Browse available offices and coworking spaces.`} />
          <meta name="robots" content="index,follow" />
        </Head>
      )}
      {!isEnglishAreaPage && (
        <SEO
          title={seoData?.title || siteTitle}
          description={seoData?.description || siteDescription}
          imageUrl={featuredImage?.node?.sourceUrl || seoData?.openGraph?.image?.secureUrl}
          url={canonicalUrl}
          useRankMath={false}
          faviconUrl={scoutSettings?.favicon?.node?.mediaItemUrl}
          seoData={null}
        />
      )}
      <Header
        menuItems={primaryMenu}
        logo={scoutSettings?.logo?.node}
        logoDarkmode={scoutSettings?.logoDarkmode?.node}
      />
      <Main>
        <Container>
          {heroContent && (
            <section className={`extra-padding-top md:px-0 ${isFaqOrPartnersPage(router, activeContent) ? 'md:max-w-[900px] md:mx-auto' : ''}`}>
                <div className="max-w-full break-words" dangerouslySetInnerHTML={{ __html: heroContent.header }} />
                <p className="text-xl max-w-3xl">
                  {heroContent.subtext}
                </p>
              {(heroContent.buttonGroup?.buttonPrimaryText || heroContent.buttonGroup?.buttonSecondaryText) && (
                <div className="flex flex-col md:flex-row gap-4 mt-8">
                  {heroContent.buttonGroup?.buttonPrimaryText && (
                    <a 
                      href={heroContent.buttonGroup?.buttonPrimaryLink?.url || '#'} 
                      target={heroContent.buttonGroup?.buttonPrimaryLink?.target || '_self'}
                      className="button-base button-primary text-sm md:text-base md:text-sm w-full md:w-auto text-center"
                    >
                      {heroContent.buttonGroup.buttonPrimaryText}
                    </a>
                  )}
                  {heroContent.buttonGroup?.buttonSecondaryText && (
                    <a 
                      href={heroContent.buttonGroup?.buttonSecondaryLink?.url || '#'} 
                      target={heroContent.buttonGroup?.buttonSecondaryLink?.target || '_self'}
                      className="button-base button-secondary text-sm md:text-base w-full md:w-auto text-center"
                    >
                      {heroContent.buttonGroup.buttonSecondaryText}
                    </a>
                  )}
                </div>
              )}
            </section>
          )}

          {sections.map((section, index) => (
            <section key={index} className="grid md:grid-cols-2 gap-0 py-10  md:py-20" id="tjeneste">
              {section.fieldGroupName === 'PagesContentSectionImageLeftTextRightLayout' ? (
                <>
                  <div className="rounded-bl-xl rounded-br-xl md:rounded-bl-xl md:rounded-tl-xl md:rounded-br-none overflow-hidden order-2 md:order-1 relative">
                    <OptimizedImage 
                      src={section.image?.node?.mediaItemUrl || ''}
                      alt={section.image?.node?.altText || section.header || ''}
                      index={index}
                      type="hero"
                      className="object-cover"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className="rounded-xl md:rounded-tr-xl md:rounded-br-xl md:rounded-tl-none md:rounded-bl-none flex flex-col justify-center bg-info-sections p-4 md:p-8 md:p-16 order-1 md:order-2">
                    <p className="text-orange-500 text-xl mb-2 md:mb-4">{section.subheader}</p>
                    <h2 className="text-3xl md:block md:text-4xl box-texts font-bold mb-2 md:mb-6">{section.header}</h2>
                    
                    {/* Desktop: Show subtext normally */}
                    <p className="text-lg md:text-xl box-texts hidden md:block">
                      {section.subtext}
                    </p>
                    
                    {/* Mobile: Show subtext in collapsible dropdown */}
                    <MobileCollapsibleSectionText 
                      subtext={section.subtext} 
                      title={section.header}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl md:rounded-tl-xl md:rounded-bl-xl flex flex-col justify-center bg-info-sections p-8 md:p-16">
                    <p className="text-orange-500 mb-4">{section.subheader}</p>
                    <h2 className="text-3xl md:text-4xl box-texts font-bold mb-6">{section.header}</h2>
                    
                    {/* Desktop: Show subtext normally */}
                    <p className="text-lg md:text-xl box-texts hidden md:block">
                      {section.subtext}
                    </p>
                    
                    {/* Mobile: Show subtext in collapsible dropdown */}
                    <MobileCollapsibleSectionText 
                      subtext={section.subtext} 
                      title={section.header}
                    />
                  </div>
                  <div className="md:rounded-tr-xl md:rounded-br-xl rounded-br-3xl md:rounded-br-none overflow-hidden relative">
                    <OptimizedImage 
                      src={section.image?.node?.mediaItemUrl || ''}
                      alt={section.image?.node?.altText || section.header || ''}
                      index={index}
                      type="hero"
                      className="object-cover"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                </>
              )}
            </section>
          ))}

          {content && (
            <div className={isFaqOrPartnersPage(router, activeContent) ? 'md:max-w-4xl md:mx-auto faq-content  py-8' : undefined}>
              <ContentWrapper content={content} />
            </div>
          )}
          
          {/* Area-specific office listings */}
          {isAreaPage && (
                          <section id={areaSlug ? areaSlug.replace(/-en$/, '').replace(/-nb$/, '') : 'offices'} className="md:py-20  md:px-0">
              <div ref={countSectionRef} className="text-center mb-12">
                <p className="text-large m-0">
                  {language === 'NB' ? 'Akkurat nå har vi' : 'Right now we have'}
                </p>
                <h2 className="text-3xl md:text-3xl lg:text-4xl xl:text-5xl font-bold m-0">
                  <span className="text-scout-gradient font-bold">
                    {appliedEmployeeCount ? filteredCount : (areaTotalCount ?? (officesLoading ? '...' : allOffices.length))}
                  </span> {language === 'NB' ? 'ledige kontorer' : 'available offices'} {title ? `i ${title}` : ''}
                </h2>
              </div>

              {/* **FILTER UI - Same as archive page** */}
              {(isEmployeeDropdownOpen || isImageTypeDropdownOpen) && !officesLoading && (
                <div
                  className="hidden md:block fixed inset-0 z-40 bg-transparent"
                  onClick={() => {
                    setIsEmployeeDropdownOpen(false);
                    setIsImageTypeDropdownOpen(false);
                  }}
                />
              )}
              <div ref={filterBarRef} className="hidden md:flex md:flex-row justify-center gap-2 md:gap-4 mb-8 px-1">
                {/* Employee count filter dropdown */}
                <div className="relative w-full md:w-auto" ref={employeeDropdownRef}>
                  <div 
                    onClick={() => !officesLoading && setIsEmployeeDropdownOpen(!isEmployeeDropdownOpen)}
                    className={`w-full md:w-auto appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-3 md:px-4 md:py-2 hover:border-scout-orange cursor-pointer text-sm md:text-base ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''} ${appliedEmployeeCount ? 'border-scout-orange' : ''}`}
                  >
                    <div className="flex items-center justify-between md:justify-start gap-2">
                      <span className={!appliedEmployeeCount ? 'text-[var(--text-primary)]' : ''}>
                        {getEmployeeFilterLabel()}
                      </span>
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </div>
                  </div>
                  {isEmployeeDropdownOpen && !officesLoading && (
                    <div className="absolute left-0 right-0 md:right-auto md:w-64 mt-2 bg-white border rounded-lg shadow-lg overflow-hidden z-50 p-4">
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t.filters.employees.employeeCount}</label>
                          <input
                            type="number"
                            min="0"
                            value={employeeCount}
                            onChange={(e) => setEmployeeCount(e.target.value)}
                            className="w-full px-3 py-3 md:py-2 border rounded-md text-sm md:text-base"
                            placeholder={t.filters.employees.employeeCount}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Image type filter - placed after employee filter */}
                <div className="relative w-full md:w-auto" ref={imageTypeDropdownRef}>
                  <div 
                    onClick={() => !officesLoading && setIsImageTypeDropdownOpen(!isImageTypeDropdownOpen)}
                    className={`w-full md:w-auto appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-3 md:px-4 md:py-2 hover:border-scout-orange cursor-pointer text-sm md:text-base ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''} ${selectedImageType !== 'alle' ? 'border-scout-orange' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={selectedImageType === 'alle' ? 'text-[var(--text-primary)]' : ''}>{getImageTypeLabel()}</span>
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </div>
                  </div>
                  {isImageTypeDropdownOpen && !officesLoading && !showStickyFilterBar && (
                    <div className="absolute left-0 right-0 md:right-auto md:w-64 mt-2 bg-white border rounded-lg shadow-lg overflow-hidden z-[100] p-2">
                      <div className="flex flex-col">
                        {imageTypes.map((type) => (
                          <button
                            key={type.value}
                            onClick={() => handleImageTypeChange(type.value)}
                            className={`w-full text-black text-left px-3 py-2 hover:bg-gray-100 rounded transition-colors ${
                              selectedImageType === type.value ? 'bg-scout-orange text-white hover:bg-scout-orange' : ''
                            }`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                                 {/* View type toggles */}
                 <div className="flex w-full md:w-auto gap-1 md:gap-2">
                   <button
                     onClick={() => !officesLoading && handleViewTypeChange(viewType === 'grid' ? 'list' : 'grid')}
                     className={`flex-1 md:flex-none appearance-none bg-[var(--bg-officecards)] border rounded-lg px-2 py-3 md:px-4 md:py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center md:justify-start gap-1 md:gap-2 text-xs md:text-base ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                     disabled={officesLoading}
                   >
                     {viewType === 'grid' ? (
                       <>
                         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                         </svg>
                         <span>{t.viewTypes.list}</span>
                       </>
                     ) : (
                       <>
                         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                         </svg>
                         <span>{t.viewTypes.grid}</span>
                       </>
                     )}
                   </button>

                   <button
                     onClick={() => !officesLoading && setShowMap(!showMap)}
                     className={`flex-1 md:flex-none appearance-none bg-[var(--bg-officecards)] border rounded-lg px-2 py-3 md:px-4 md:py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center md:justify-start gap-1 md:gap-2 text-xs md:text-base ${showMap ? 'border-scout-orange' : ''} ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                     disabled={officesLoading}
                     aria-label={showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}
                   >
                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                     </svg>
                     <span>{showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}</span>
                   </button>

                   {/* Reset all filters button */}
                   <button
                     onClick={handleResetAllFilters}
                     className={`flex-1 md:flex-none appearance-none bg-[var(--bg-officecards)] border rounded-lg px-4 py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center md:justify-start gap-2 ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                     disabled={officesLoading}
                   >
                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                     </svg>
                     <span>{t.filters.reset}</span>
                   </button>
                 </div>
               </div>

              {/* Mobile sticky filter bar: only show after passing the count header */}
              {showMobileFilterBar && (
                <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-transparent border-none shadow-none px-3 py-2 pb-8 flex flex-col gap-2">
                  <div>
                    <button
                      onClick={handleResetAllFilters}
                      className="self-start inline-flex items-center text-xs px-3 py-1.5 appearance-none bg-[var(--bg-primary)] border rounded-full hover:border-scout-orange shadow-md"
                    >
                      {t.filters.reset}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                  {/* View toggle */}
                  <button
                      onClick={() => !officesLoading && handleViewTypeChange(viewType === 'grid' ? 'list' : 'grid')}
                      className={`flex-1 appearance-none bg-[var(--bg-primary)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center gap-2 text-sm shadow-md ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                    disabled={officesLoading}
                  >
                    {viewType === 'grid' ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                        <span>{t.viewTypes.list}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                        <span>{t.viewTypes.grid}</span>
                      </>
                    )}
                  </button>

                  {/* Map toggle */}
                    <button
                      onClick={() => { if (!officesLoading) { setShowMap(!showMap); } }}
                      className={`flex-1 appearance-none bg-[var(--bg-primary)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center gap-2 text-sm shadow-md ${showMap ? 'border-scout-orange' : ''} ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                    disabled={officesLoading}
                    aria-label={showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                    <span>{showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}</span>
                  </button>

                  {/* Open filter */}
                    <button
                      onClick={() => setIsMobileFilterOpen(true)}
                      className="flex-1 appearance-none bg-[var(--bg-primary)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center justify-center gap-2 text-sm shadow-md"
                      aria-label={router.locale === 'en' ? 'Open filters' : 'Åpne filter'}
                    >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M4 9h16M6 14h12M8 19h8" />
                    </svg>
                    <span>{router.locale === 'en' ? 'Filter' : 'Filtrer'}</span>
                    {(appliedEmployeeCount ? 1 : 0) > 0 && (
                      <span className="bg-scout-gradient text-white rounded-full px-2 py-0.5 text-[10px]">
                        {appliedEmployeeCount ? 1 : 0}
                      </span>
                    )}
                  </button>
                  </div>

                  <></>
                </div>
              )}

              {/* Desktop compact sticky filter tab bar */}
              {showStickyFilterBar && !isMobile && (
                <div className="hidden md:block fixed top-20 left-0 right-0 z-50 bg-[var(--bg-officecards)] border-b border-[var(--border-color)] shadow-sm">
                  <div className="max-w-7xl mx-auto px-2 py-2 flex items-center gap-2 justify-center">
                    {/* Employee count */}
                    <div className="relative w-full md:w-[220px]" ref={employeeDropdownRef}>
                      <div 
                        onClick={() => !officesLoading && setIsEmployeeDropdownOpen(!isEmployeeDropdownOpen)}
                        className={`w-full appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-2 md:px-4 md:py-2 pr-8 hover:border-scout-orange cursor-pointer text-sm ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''} ${appliedEmployeeCount ? 'border-scout-orange' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={!appliedEmployeeCount ? 'text-[var(--text-primary)]' : ''}>
                            {getEmployeeFilterLabel()}
                          </span>
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                          </svg>
                        </div>
                      </div>
                      {isEmployeeDropdownOpen && !officesLoading && (
                        <div className="absolute left-0 right-0 md:right-auto md:w-64 mt-2 bg-white border rounded-lg shadow-lg overflow-hidden z-60 p-4">
                          <div className="flex flex-col gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t.filters.employees.employeeCount}</label>
                              <input
                                type="number"
                                min="0"
                                value={employeeCount}
                                onChange={(e) => setEmployeeCount(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md"
                                placeholder={t.filters.employees.employeeCount}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Image type filter - placed after employee filter */}
                    <div className="relative w-full md:w-auto" ref={imageTypeDropdownRef}>
                      <div 
                        onClick={() => !officesLoading && setIsImageTypeDropdownOpen(!isImageTypeDropdownOpen)}
                        className={`w-full md:w-auto appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-3 md:px-4 md:py-2 hover:border-scout-orange cursor-pointer text-sm md:text-base ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''} ${selectedImageType !== 'alle' ? 'border-scout-orange' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={selectedImageType === 'alle' ? 'text-[var(--text-primary)]' : ''}>{getImageTypeLabel()}</span>
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                          </svg>
                        </div>
                      </div>
                      {isImageTypeDropdownOpen && !officesLoading && showStickyFilterBar && (
                        <div className="absolute left-0 right-0 md:right-auto md:w-64 mt-2 bg-white border rounded-lg shadow-lg overflow-hidden z-[100] p-2">
                          <div className="flex flex-col">
                            {imageTypes.map((type) => (
                              <button
                                key={type.value}
                                onClick={() => handleImageTypeChange(type.value)}
                                className={`w-full text-black text-left px-3 py-2 hover:bg-gray-100 rounded transition-colors ${
                                  selectedImageType === type.value ? 'bg-scout-orange text-white hover:bg-scout-orange' : ''
                                }`}
                              >
                                {type.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* View toggle */}
                    <button
                      onClick={() => !officesLoading && handleViewTypeChange(viewType === 'grid' ? 'list' : 'grid')}
                      className={`appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center gap-2 text-sm ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                      disabled={officesLoading}
                    >
                      {viewType === 'grid' ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                          </svg>
                          <span>{t.viewTypes.list}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                          </svg>
                          <span>{t.viewTypes.grid}</span>
                        </>
                      )}
                    </button>

                    {/* Map toggle */}
                    <button
                      onClick={() => { if (!officesLoading) { setShowMap(!showMap); setTimeout(() => { const anchor = document.querySelector('.area-map-scroll-anchor'); if (anchor) { anchor.scrollIntoView({ behavior: 'smooth', block: 'start' }); window.scrollBy({ top: -120, left: 0, behavior: 'instant' }); } }, 50); } }}
                      className={`appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center gap-2 text-sm ${showMap ? 'border-scout-orange' : ''} ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                      disabled={officesLoading}
                      aria-label={showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      <span>{showMap ? t.viewTypes.hideMap : t.viewTypes.showMap}</span>
                    </button>

                    {/* Reset */}
                    <button
                      onClick={handleResetAllFilters}
                      className={`appearance-none bg-[var(--bg-officecards)] border rounded-lg px-3 py-2 hover:border-scout-orange cursor-pointer flex items-center gap-2 text-sm ${officesLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                      disabled={officesLoading}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>{t.filters.reset}</span>
                    </button>
                  </div>
                </div>
              )}

      {/* Mobile filter overlay - Full screen */}
      {isMobileFilterOpen && (
        <>
          {/* Background overlay */}
          <div 
            className="md:hidden fixed inset-0 z-[9998] bg-black/50"
            onClick={() => setIsMobileFilterOpen(false)}
          />
          {/* Filter modal */}
          <div className="md:hidden fixed inset-0 z-[9999] bg-[var(--bg-primary)] flex flex-col"
               style={{ 
                 height: '100vh',
                 height: '100dvh', // Dynamic viewport height for mobile browsers
                 paddingBottom: 'env(safe-area-inset-bottom)' 
               }}
               onClick={(e) => e.stopPropagation()}>
          
          {/* Floating close button - positioned absolutely */}
          <button
            onClick={() => setIsMobileFilterOpen(false)}
            aria-label={router.locale === 'en' ? 'Close filters' : 'Lukk filter'}
            className="absolute top-4 right-4 z-[10000] p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} style={{ color: 'var(--text-primary)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Header */}
          <div className="flex items-center justify-center p-4 border-b border-[var(--border-color)] flex-shrink-0" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <h2 className="font-semibold text-lg">{router.locale === 'en' ? 'Find offices' : 'Finn kontorer'}</h2>
          </div>
          
          {/* Selected filters display */}
          {(appliedEmployeeCount || selectedImageType !== 'all') && (
            <div className="p-4 border-b border-[var(--border-color)] flex-shrink-0">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {appliedEmployeeCount && (
                  <div className="flex items-center bg-[var(--bg-secondary)] rounded-full px-3 py-1 text-sm flex-shrink-0">
                    <span>{appliedEmployeeCount} {t.filters.employees.count}</span>
                    <button
                      onClick={() => {
                        setAppliedEmployeeCount(null);
                        setEmployeeCount('');
                        setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                      }}
                      className="ml-2 p-0.5 hover:bg-gray-200 rounded-full"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                {selectedImageType !== 'all' && (
                  <div className="flex items-center bg-[var(--bg-secondary)] rounded-full px-3 py-1 text-sm flex-shrink-0">
                    <span>{imageTypes.find(type => type.value === selectedImageType)?.label}</span>
                    <button
                      onClick={() => setSelectedImageType('all')}
                      className="ml-2 p-0.5 hover:bg-gray-200 rounded-full"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Content area - Scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-4">
              {/* Image type filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-400">{language === 'NB' ? 'Velg bildetype' : 'Select image type'}</label>
                <select
                  value={selectedImageType}
                  onChange={(e) => handleImageTypeChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base bg-white text-gray-900"
                  disabled={officesLoading}
                >
                  {imageTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Employee count filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-400">{t.filters.employees.employeeCount}</label>
                <input
                  type="number"
                  min="0"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base bg-white text-gray-900 placeholder-gray-500"
                  placeholder={t.filters.employees.employeeCount}
                />
              </div>
            </div>
          </div>
          
          {/* Fixed bottom section with button */}
          <div className="flex-shrink-0 p-4 border-t border-[var(--border-color)] bg-[var(--bg-primary)]">
            <div className="flex items-center gap-3">
              {/* Clear all button - left side */}
              {(appliedEmployeeCount || selectedImageType !== 'all') && (
                <button
                  onClick={() => {
                    // Clear all filters
                    setAppliedEmployeeCount(null);
                    setEmployeeCount('');
                    setSelectedImageType('all');
                    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                  }}
                  className="flex items-center justify-center w-12 h-12 bg-[var(--bg-secondary)] hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                  aria-label={router.locale === 'en' ? 'Clear all filters' : 'Fjern alle filtre'}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
              
              {/* Main button - takes remaining space */}
              <button
                onClick={() => {
                  setIsMobileFilterOpen(false);
                  // Scroll to results section
                  setTimeout(() => {
                    const resultsSection = countSectionRef.current;
                    if (resultsSection) {
                      resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="flex-1 bg-black text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {router.locale === 'en' 
                  ? `Show ${displayedOffices.length} results` 
                  : `Se ${displayedOffices.length} resultater`
                }
              </button>
            </div>
          </div>
          </div>
        </>
      )}

              {/* **MAP COMPONENT - Same as archive page** */}
              {showMap && !officesLoading && displayedOffices.length > 0 && (
                <div className={`${isMobile ? 'hidden' : 'mb-8'} area-map-scroll-anchor`}>
                  {/* Desktop modal-like overlay */}
                  <div className="hidden md:block fixed inset-0 z-[60] bg-black/60"></div>
                  <div className="hidden md:flex fixed z-[70] inset-0 items-stretch justify-center pointer-events-none">
                    <div className="bg-[var(--bg-primary)] w-full h-full overflow-hidden pointer-events-auto flex flex-col relative">
                      {/* Floating close button over the map */}
                      <button
                        onClick={() => setShowMap(false)}
                        className="absolute top-[5rem] right-4 z-[9999] p-3 bg-white hover:bg-gray-100 rounded-full shadow-lg transition-all duration-200 border border-gray-200"
                        aria-label="Close map"
                        style={{ minWidth: '48px', minHeight: '48px' }}
                      >
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="w-full h-full">
                        <ArchiveMap filteredOffices={displayedOffices} variant="modal" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Add a separate map container for mobile that will be positioned fixed */}
              {showMap && !officesLoading && isMobile && displayedOffices.length > 0 && (
                <ArchiveMap filteredOffices={displayedOffices} />
              )}
              
              {officesLoading ? (
                <div className={`grid gap-6 ${viewType === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className={`bg-white rounded-lg shadow-sm border animate-pulse ${viewType === 'grid' ? 'flex flex-col' : 'flex flex-col md:flex-row'}`}>
                      <div className={`bg-gray-200 ${viewType === 'grid' ? 'w-full h-48 rounded-t-lg' : 'w-full h-48 md:w-1/3 md:h-32 rounded-t-lg md:rounded-l-lg md:rounded-t-none'}`}></div>
                      <div className={`p-4 ${viewType === 'list' ? 'md:w-2/3' : ''}`}>
                        <div className="h-6 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayedOffices.length > 0 ? (
                <>
                  <div className={viewType === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'flex flex-col gap-6'}>
                    {displayedOffices.map((office, officeIndex) => {
                      // Use simplified image processing
                      const { mainImage, thumbnailImages, allImages } = processOfficeImages(
                        office, 
                        selectedImageType, 
                        hoveredImages
                      );

                      // Use stable key to prevent unnecessary remounts and image reloads
                      const uniqueKey = `office-${office.id}`;

                      return (
                        <article key={uniqueKey} className={`bg-[var(--bg-officecards)] rounded-xl overflow-hidden shadow-sm hover:shadow-md dark:shadow-gray-700/20 dark:hover:shadow-gray-700/30 transition-shadow h-full ${
                          viewType === 'list' ? (isMobile ? 'flex items-stretch' : 'flex items-center') : 'flex flex-col'
                        }`}>
                          <div className={`relative ${viewType === 'list' ? (isMobile ? 'w-1/3 flex-shrink-0' : 'w-1/3 flex-shrink-0 h-full') : ''} overflow-hidden`}>
                            {/* Main image - navigates to office page */}
                            <div 
                              className={`relative cursor-pointer group ${viewType === 'list' ? (isMobile ? 'h-full' : 'aspect-[2/1]') : 'aspect-[3/2]'} w-full overflow-hidden`}
                              onClick={(e) => {
                                e.preventDefault();
                                const url = generateOfficeUrl(office, router.locale);
                                const isMobile = window.innerWidth < 768;
                                if (isMobile) {
                                  window.location.href = url;
                                } else {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                            >
                              <OptimizedImage
                                src={mainImage?.bilde?.node?.mediaItemUrl}
                                alt={office.title}
                                index={officeIndex}
                                type={viewType === 'list' ? 'office-card' : 'archive-card'}
                                viewType={viewType}
                                isMobile={isMobile}
                                className="transition-transform group-hover:scale-105"
                                style={{ objectFit: 'cover' }}
                              />
                              
                              {/* Hover overlay - arrow icon for navigation */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-3 shadow-lg">
                                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              </div>

                              {!(viewType === 'list' && isMobile) && (
                                <div className="absolute top-4 right-4 px-2 py-1 rounded-full text-[9px] md:text-xs font-medium office-card-area-tag">
                                  {office.kontorer?.lokasjon?.omrade?.nodes?.[0]?.name || office.terms?.nodes?.[0]?.name || office.kontorer?.lokasjon?.kart?.city || 'Area'}
                                </div>
                              )}
                              
                              {allImages.length > 1 && !(viewType === 'list' && isMobile) && (
                                <div className="absolute bottom-4 right-4 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                                  +{allImages.length - 1} bilder
                                </div>
                              )}
                              
                              {/* Gallery button for accessing lightbox */}
                              {allImages.length > 1 && (
                                <button
                                  className="absolute bottom-4 left-2 p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleLightbox(true, allImages, 0);
                                  }}
                                  aria-label="Åpne galleri"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              )}
                            </div>

                            {/* Thumbnail row with gallery triggers */}
                            {thumbnailImages.length > 0 && !(viewType === 'list' && isMobile) && (
                              <div className="flex gap-2 p-1">
                                {thumbnailImages.map((image, idx) => (
                                  <div 
                                    key={idx} 
                                    className="relative aspect-[4/3] w-1/3 cursor-pointer group"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (allImages.length > 0) {
                                        toggleLightbox(true, allImages, idx + 1); // Start at clicked thumbnail
                                      }
                                    }}
                                  >
                                <OptimizedImage
                                  src={image.bilde.node.mediaItemUrl || image.bilde.node.sourceUrl}
                                  alt={`${office.title} - bilde ${idx + 2}`}
                                  index={1000 + officeIndex * 10 + idx}
                                  type="office-thumbnail"
                                  priority={false}
                                  className="rounded transition-transform group-hover:scale-105"
                                />
                                    
                                    {/* Hover overlay for thumbnails */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center rounded">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full p-1 shadow-lg">
                                        <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <a 
                            href={generateOfficeUrl(office, router.locale)} 
                            className={`${viewType === 'list' ? 'px-6 py-2 flex-1 min-w-0' : 'p-6 flex-1 flex flex-col'}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                              <div className="flex-1 flex flex-col">
                                <h2 className={`font-bold ${viewType === 'list' ? 'md:text-xl text-[14px]' : 'text-xl'} mb-2 office-card-title truncate`}>
                                  {office.title}
                                </h2>
                                {(() => {
                                  const k = office.kontorer?.lokasjon?.kart || {};
                                  const address = k.streetAddress || (k.streetName && k.streetNumber ? `${k.streetName} ${k.streetNumber}` : null) || k.city;
                                  if (!address) return null;
                                  return (
                                    <div className="flex items-center gap-1 text-sm mb-2 office-card-address">
                                      <svg className="w-4 h-4 text-orange-500 opacity-70 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11a3 3 0 100-6 3 3 0 000 6z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22s8-4.5 8-11a8 8 0 10-16 0c0 6.5 8 11 8 11z" />
                                      </svg>
                                      <span className="truncate block max-w-full text-orange-500 opacity-70">{address}</span>
                                    </div>
                                  );
                                })()}
                                
                                {(viewType !== 'list' || (viewType === 'list' && !isMobile)) && (
                                  <div 
                                    className="text-sm mb-4 line-clamp-2 kontorkort-beskrivelse archive-description office-card-description"
                                    dangerouslySetInnerHTML={{ __html: office.kontorer?.informasjon?.kortBeskrivelse }}
                                  />
                                )}
                                <div className="flex flex-wrap gap-2 pt-3 mt-auto">
                                {office.kontorer?.informasjon?.type && (
                                  <span className="inline-flex items-center office-card-badge rounded-full member-bio">
                                    <span className={`font-medium ${viewType === 'list' && isMobile ? 'text-[12px] px-2.5 py-1' : 'text-[12px] px-3 py-1.5'}`}>
                                      {(() => {
                                        const type = office.kontorer.informasjon.type;
                                        const typeString = Array.isArray(type) ? type[0] : type;
                                        const normalizedType = typeString?.toLowerCase?.()?.trim();
                                        const officeTypes = {
                                          'coworking': language === 'NB' ? 'Coworking' : 'Coworking',
                                          'fremleie': language === 'NB' ? 'Fremleie' : 'Sublease',
                                          'kontor': language === 'NB' ? 'Kontor' : 'Direct lease'
                                        };
                                          return officeTypes[normalizedType] || typeString;
                                      })()}
                                    </span>
                                  </span>
                                )}
                                {office.kontorer?.informasjon?.areal && (() => {
                                  // Don't show square meters for coworking spaces only
                                  const typeValue = office.kontorer?.informasjon?.type;
                                  const typeString = Array.isArray(typeValue) ? typeValue[0] : typeValue;
                                  const normalized = typeString?.toLowerCase?.()?.trim();
                                  const isCoworking = normalized === 'coworking';
                                  
                                  if (isCoworking) {
                                    return null;
                                  }
                                  
                                  return (
                                    <span className="inline-flex items-center office-card-badge rounded-full member-bio">
                                      <span className={`font-medium ${viewType === 'list' && isMobile ? 'text-[12px] px-2.5 py-1' : 'text-[12px] px-3 py-1.5'}`}>
                                        {office.kontorer.informasjon.areal}m²
                                      </span>
                                    </span>
                                  );
                                })()}
                                {(() => {
                                  const typeValue = office.kontorer?.informasjon?.type;
                                  const typeString = Array.isArray(typeValue) ? typeValue[0] : typeValue;
                                  const normalized = typeString?.toLowerCase?.()?.trim();
                                  const isCoworkingLike = normalized === 'coworking' || normalized === 'kontorhotell';
                                  if (isCoworkingLike && office.kontorer?.informasjon?.kontorplasser) {
                                    return (
                                      <span className="inline-flex items-center office-card-badge rounded-full member-bio">
                                        <span className={`font-medium ${viewType === 'list' && isMobile ? 'text-[12px] px-2.5 py-1' : 'text-[12px] px-3 py-1.5'}`}>
                                          {office.kontorer.informasjon.kontorplasser} {language === 'NB' ? 'kontorplasser' : 'office spaces'}
                                        </span>
                                      </span>
                                    );
                                  }
                                  const from = office.kontorer?.antallAnsatte?.fra;
                                  const to = office.kontorer?.antallAnsatte?.til;
                                  if (from || to) {
                                    return (
                                      <span className="inline-flex items-center office-card-badge rounded-full member-bio">
                                        <span className={`font-medium ${viewType === 'list' && isMobile ? 'text-[12px] px-2.5 py-1' : 'text-[12px] px-3 py-1.5'}`}>
                                          {(from ?? '?')}–{(to ?? '?')} {language === 'NB' ? 'ansatte' : 'employees'}
                                        </span>
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                                {(office.kontorer?.informasjon?.leie || office.kontorer?.informasjon?.manedligKostnadPerPlass || office.kontorer?.informasjon?.leiePerPlass) && (
                                  <span className="inline-flex items-center office-card-badge rounded-full member-bio">
                                    <span className={`font-medium ${viewType === 'list' && isMobile ? 'text-[12px] px-2.5 py-1' : 'text-[12px] px-3 py-1.5'}`}>
                                      {(language === 'NB' ? 'Est. pris/mnd' : 'Est. price/month')}: {new Intl.NumberFormat('no-NO').format(office.kontorer.informasjon.leie || office.kontorer.informasjon.manedligKostnadPerPlass || office.kontorer.informasjon.leiePerPlass)} kr
                                    </span>
                                  </span>
                                )}
                                </div>
                              </div>
                          </a>
                        </article>
                      );
                    })}
                  </div>
                  
                  {/* Load more button */}
                  {hasMoreToShow && (
                    <div className="text-center mt-8">
                      <button
                        onClick={handleLoadMore}
                        className="button-base button-indigo"
                      >
                        {language === 'NB' ? 'Last flere kontorer' : 'Load more offices'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-lg text-gray-600">
                    {language === 'NB' 
                      ? 'Ingen ledige kontorer tilgjengelig for øyeblikket.' 
                      : 'No available offices at the moment.'
                    }
                  </p>
                  <Link 
                    href={language === 'NB' ? '/kontorer' : '/en/offices'} 
                    locale={false}
                    className="button-base button-primary mt-4 inline-block text-sm md:text-base"
                  >
                    {language === 'NB' ? 'Se alle kontorer' : 'View all offices'}
                  </Link>
                </div>
              )}
              
            </section>
          )}
          
          {isSpecialPage ? (
            <>
              <ServiceSection />
              <Banner />
            </>
          ) : (
            <ContactFormSection variant="form" successRedirect="/kontakt-sendt" />
          )}
        </Container>
      </Main>
      <Footer menuItems={footerMenu} logo={scoutSettings?.logoDarkmode?.node} />
      <ContactPopup />
      
      {/* Lightbox Overlay - same as archive page */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center p-0 md:p-4">
          <div className="relative flex flex-col md:flex-row w-full max-w-full md:max-w-7xl h-full md:h-[85vh] shadow-xl overflow-hidden bg-white bg-opacity-0 rounded-xl">
            
            {/* Close button at absolute top right of modal */}
            <button
              className="fixed top-4 right-4 z-[10000] bg-gray-900 bg-opacity-80 text-white rounded-full p-2 shadow hover:bg-opacity-100 transition"
              onClick={() => toggleLightbox(false)}
              aria-label="Lukk galleri"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {/* Navigation arrows */}
            {lightboxImages.length > 1 && (
              <>
                {/* Desktop left arrow - consistent dark styling */}
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-gray-900 bg-opacity-80 text-white rounded-full p-3 shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200 md:block hidden"
                  onClick={() => navigateLightbox('prev')}
                  aria-label="Forrige bilde"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                
                {/* Desktop right arrow - positioned to avoid thumbnail sidebar */}
                <button
                  className="absolute right-[160px] top-1/2 -translate-y-1/2 z-10 bg-gray-900 bg-opacity-80 text-white rounded-full p-3 shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200 md:block hidden"
                  onClick={() => navigateLightbox('next')}
                  aria-label="Neste bilde"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                
                {/* Mobile arrows overlayed - consistent dark styling */}
                <div className="flex md:hidden justify-between absolute inset-x-0 top-1/2 -translate-y-1/2 px-4 z-10">
                  <button
                    className="bg-gray-900 bg-opacity-80 text-white rounded-full p-3 shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200"
                    onClick={() => navigateLightbox('prev')}
                    aria-label="Forrige bilde"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button
                    className="bg-gray-900 bg-opacity-80 text-white rounded-full p-3 shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200"
                    onClick={() => navigateLightbox('next')}
                    aria-label="Neste bilde"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </>
            )}

            {/* Main image container */}
            <div className="relative flex-1 flex flex-col justify-center w-full">
              <img
                src={lightboxImages[lightboxImageIndex]?.processedLightboxUrl || getLightboxImageUrl(lightboxImages[lightboxImageIndex])}
                alt={'Office image'}
                className="w-full h-full object-contain rounded-xl select-none"
                style={{ 
                  imageRendering: 'high-quality'
                }}
                tabIndex={0}
                aria-label="Bilde i galleri"
              />
              {/* Caption and image count overlay */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black bg-opacity-40 text-white text-xs md:text-base px-3 py-1 rounded shadow text-center max-w-[90%]">
                {lightboxImages[lightboxImageIndex]?.bilde?.node?.altText || 'Office image'}
                <span className="ml-2 text-[10px] md:text-sm text-gray-200">{lightboxImageIndex + 1}/{lightboxImages.length}</span>
              </div>
                          </div>
            
            {/* Mobile horizontal thumbnails in modal */}
            <div className="flex md:hidden gap-2 mt-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent w-full px-2">
              {lightboxImages.map((image, idx) => (
                <button
                  key={idx}
                  onClick={() => setLightboxImageIndex(idx)}
                  className={`w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 ${lightboxImageIndex === idx ? 'border-scout-orange' : 'border-transparent'} focus:outline-none focus:ring-2 focus:ring-scout-orange`}
                  aria-label={`Vis bilde ${idx + 1}`}
                  tabIndex={0}
                >
                  <img
                    src={image.processedThumbnailUrl || getThumbnailUrl(image)}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                    style={{ imageRendering: 'high-quality' }}
                  />
                </button>
              ))}
            </div>
            {/* Desktop vertical thumbnail list in modal - optimized for larger main image */}
            <div className="hidden md:flex w-[140px] ml-3 pr-2 h-full items-center justify-center">
              <div className="flex flex-col gap-2" style={{ height: 'auto' }}>
                {(() => {
                  const [start, end] = getModalThumbWindow();
                  return lightboxImages.slice(start, end).map((image, idx) => {
                    const realIdx = start + idx;
                    return (
                      <button
                        key={realIdx}
                        ref={el => modalThumbRefs.current[realIdx] = el}
                        onClick={() => setLightboxImageIndex(realIdx)}
                        className={`w-full aspect-video rounded-lg overflow-hidden border-2 transition focus:outline-none focus:ring-2 focus:ring-scout-orange ${lightboxImageIndex === realIdx ? 'border-scout-orange ring-2 ring-scout-orange' : 'border-transparent'}`}
                        aria-label={`Vis bilde ${realIdx + 1}`}
                        tabIndex={0}
                      >
                        <img
                          src={image.processedThumbnailUrl || getThumbnailUrl(image)}
                          alt={`Thumbnail ${realIdx + 1}`}
                          className="w-full h-full object-cover"
                          style={{ imageRendering: 'high-quality' }}
                        />
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

Component.variables = (seedQuery, ctx) => {
  return {
    databaseId: seedQuery?.databaseId,
    asPreview: ctx?.asPreview
  };
};

// Optimized query - navigation data comes from SharedLayoutProvider
Component.query = gql`
  ${BlogInfoFragment}
  ${PageSEOFragment}
  ${PageBaseFragment}
  ${PageContentFragment}
  ${PageTranslationContentFragment}
  query GetPageData(
    $databaseId: ID!
    $asPreview: Boolean = false
  ) {
    page(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      ...PageBaseFragment
      ...PageSEOFragment
      ...PageContentFragment
      translations {
        ...PageBaseFragment
        ...PageSEOFragment
        ...PageTranslationContentFragment
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;